import React, { useState, useEffect, useContext } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { AuthContext } from '../../../contexts/AuthContext';
import { journalEntriesService } from '../../../services/journalEntriesService';
import { chartOfAccountsService } from '../../../services/chartOfAccountsService';

const ManualJournalEntry = () => {
  const { user, userProfile } = useContext(AuthContext);
  const [journalEntries, setJournalEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    reference_number: '',
    description: '',
    entry_date: new Date()?.toISOString()?.split('T')?.[0],
    notes: ''
  });
  
  const [lineItems, setLineItems] = useState([
    { account_id: '', description: '', debit_amount: '', credit_amount: '' }
  ]);

  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  });

  useEffect(() => {
    if (userProfile?.organizations?.[0]?.id) {
      loadData();
    }
  }, [userProfile]);

  const loadData = async () => {
    setLoading(true);
    try {
      const organizationId = userProfile?.organizations?.[0]?.id;
      
      // Load journal entries
      const entriesResult = await journalEntriesService?.getJournalEntries(organizationId, {
        page: 1,
        limit: 50,
        ...filters
      });
      
      if (entriesResult?.data) {
        setJournalEntries(entriesResult?.data);
      }

      // Load chart of accounts
      const accountsResult = await chartOfAccountsService?.getChartOfAccounts(organizationId);
      if (accountsResult?.data) {
        setAccounts(accountsResult?.data?.filter(account => account?.allows_posting === true) || []);
      }
    } catch (error) {
      console.error('Load data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!user?.id || !userProfile?.organizations?.[0]?.id) return;

    setCreating(true);
    try {
      const organizationId = userProfile?.organizations?.[0]?.id;
      
      // Create journal entry
      const entryResult = await journalEntriesService?.createJournalEntry({
        organization_id: organizationId,
        created_by: user?.id,
        ...formData
      });

      if (entryResult?.error) {
        alert('Error creating journal entry: ' + entryResult?.error);
        return;
      }

      // Add line items
      const validLineItems = lineItems?.filter(item => 
        item?.account_id && 
        item?.description && 
        (parseFloat(item?.debit_amount) > 0 || parseFloat(item?.credit_amount) > 0)
      );

      if (validLineItems?.length > 0) {
        const lineItemsResult = await journalEntriesService?.addLineItems(
          entryResult?.data?.id, 
          validLineItems
        );

        if (lineItemsResult?.error) {
          alert('Error adding line items: ' + lineItemsResult?.error);
          return;
        }
      }

      // Reset form
      setFormData({
        reference_number: '',
        description: '',
        entry_date: new Date()?.toISOString()?.split('T')?.[0],
        notes: ''
      });
      setLineItems([
        { account_id: '', description: '', debit_amount: '', credit_amount: '' }
      ]);
      setShowForm(false);

      // Reload data
      await loadData();

      alert('Journal entry created successfully!');
    } catch (error) {
      console.error('Create journal entry error:', error);
      alert('Failed to create journal entry');
    } finally {
      setCreating(false);
    }
  };

  const handleApprove = async (entryId) => {
    if (!user?.id) return;
    
    try {
      const result = await journalEntriesService?.approveJournalEntry(entryId, user?.id);
      if (result?.error) {
        alert('Error approving entry: ' + result?.error);
        return;
      }
      
      await loadData();
      alert('Journal entry approved successfully!');
    } catch (error) {
      console.error('Approve error:', error);
      alert('Failed to approve journal entry');
    }
  };

  const handlePost = async (entryId) => {
    if (!user?.id) return;
    
    try {
      const result = await journalEntriesService?.postJournalEntry(entryId, user?.id);
      if (result?.error) {
        alert('Error posting entry: ' + result?.error);
        return;
      }
      
      await loadData();
      alert('Journal entry posted successfully!');
    } catch (error) {
      console.error('Post error:', error);
      alert('Failed to post journal entry');
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { account_id: '', description: '', debit_amount: '', credit_amount: '' }]);
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems?.filter((_, i) => i !== index));
  };

  const updateLineItem = (index, field, value) => {
    const updated = lineItems?.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    setLineItems(updated);
  };

  const calculateTotals = () => {
    const totalDebit = lineItems?.reduce((sum, item) => sum + (parseFloat(item?.debit_amount) || 0), 0);
    const totalCredit = lineItems?.reduce((sum, item) => sum + (parseFloat(item?.credit_amount) || 0), 0);
    return { totalDebit, totalCredit };
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      pending_review: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      posted: 'bg-blue-100 text-blue-800',
      reversed: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors?.[status] || colors?.draft}`}>
        {status?.replace('_', ' ')?.toUpperCase()}
      </span>
    );
  };

  const { totalDebit, totalCredit } = calculateTotals();
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Manual Journal Entries</h2>
          <p className="text-muted-foreground mt-1">Create and manage manual journal entries with balance validation</p>
        </div>
        <Button 
          onClick={() => setShowForm(!showForm)}
          iconName="Plus"
          iconPosition="left"
          className="mt-4 sm:mt-0"
        >
          New Journal Entry
        </Button>
      </div>
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select
            value={filters?.status}
            onChange={(value) => setFilters({...filters, status: value})}
            placeholder="Filter by status"
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'draft', label: 'Draft' },
              { value: 'pending_review', label: 'Pending Review' },
              { value: 'approved', label: 'Approved' },
              { value: 'posted', label: 'Posted' },
              { value: 'reversed', label: 'Reversed' }
            ]}
          />
          <Input
            type="date"
            value={filters?.dateFrom}
            onChange={(e) => setFilters({...filters, dateFrom: e?.target?.value})}
            placeholder="From date"
          />
          <Input
            type="date"
            value={filters?.dateTo}
            onChange={(e) => setFilters({...filters, dateTo: e?.target?.value})}
            placeholder="To date"
          />
          <div className="flex space-x-2">
            <Input
              value={filters?.search}
              onChange={(e) => setFilters({...filters, search: e?.target?.value})}
              placeholder="Search entries..."
              className="flex-1"
            />
            <Button onClick={loadData} variant="outline" iconName="Search">
              Search
            </Button>
          </div>
        </div>
      </div>
      {/* Journal Entry Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">New Journal Entry</h3>
          
          {/* Header Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <Input
              label="Reference Number"
              value={formData?.reference_number}
              onChange={(e) => setFormData({...formData, reference_number: e?.target?.value})}
              placeholder="REF-001"
            />
            <Input
              label="Entry Date"
              type="date"
              value={formData?.entry_date}
              onChange={(e) => setFormData({...formData, entry_date: e?.target?.value})}
              required
            />
            <Input
              label="Description"
              value={formData?.description}
              onChange={(e) => setFormData({...formData, description: e?.target?.value})}
              placeholder="Journal entry description"
              required
            />
          </div>

          {/* Line Items */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-md font-medium text-foreground">Line Items</h4>
              <Button 
                type="button"
                onClick={addLineItem}
                variant="outline"
                size="sm"
                iconName="Plus"
              >
                Add Line
              </Button>
            </div>

            <div className="space-y-3">
              {lineItems?.map((item, index) => (
                <div key={index} className="grid grid-cols-1 lg:grid-cols-5 gap-3 p-3 border border-border rounded-lg">
                  <Select
                    value={item?.account_id}
                    onChange={(value) => updateLineItem(index, 'account_id', value)}
                    placeholder="Select account"
                    options={accounts?.map(account => ({
                      value: account?.id,
                      label: `${account?.account_code} - ${account?.account_name}`
                    })) || []}
                    required
                  />
                  <Input
                    value={item?.description}
                    onChange={(e) => updateLineItem(index, 'description', e?.target?.value)}
                    placeholder="Line description"
                    required
                  />
                  <Input
                    type="number"
                    step="0.01"
                    value={item?.debit_amount}
                    onChange={(e) => updateLineItem(index, 'debit_amount', e?.target?.value)}
                    placeholder="Debit amount"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    value={item?.credit_amount}
                    onChange={(e) => updateLineItem(index, 'credit_amount', e?.target?.value)}
                    placeholder="Credit amount"
                  />
                  <Button
                    type="button"
                    onClick={() => removeLineItem(index)}
                    variant="outline"
                    size="sm"
                    iconName="Trash2"
                    className="text-red-600 hover:bg-red-50"
                  />
                </div>
              ))}
            </div>

            {/* Balance Check */}
            <div className={`mt-4 p-3 rounded-lg ${isBalanced ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex justify-between items-center text-sm">
                <span className="font-medium">Balance Check:</span>
                <div className="flex space-x-4">
                  <span>Total Debit: ${totalDebit?.toFixed(2)}</span>
                  <span>Total Credit: ${totalCredit?.toFixed(2)}</span>
                  <span className={isBalanced ? 'text-green-600' : 'text-red-600'}>
                    {isBalanced ? '✓ Balanced' : '✗ Out of Balance'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
            <textarea
              value={formData?.notes}
              onChange={(e) => setFormData({...formData, notes: e?.target?.value})}
              placeholder="Additional notes..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              onClick={() => setShowForm(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={creating || !isBalanced}
              iconName={creating ? "Loader2" : "Save"}
              className={creating ? "animate-spin" : ""}
            >
              {creating ? 'Creating...' : 'Create Entry'}
            </Button>
          </div>
        </form>
      )}
      {/* Journal Entries List */}
      <div className="bg-card border border-border rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Journal Entries</h3>
        </div>
        
        {journalEntries?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Journal Entries</h3>
            <p className="text-muted-foreground">Create your first journal entry to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {journalEntries?.map((entry) => (
              <div key={entry?.id} className="px-6 py-4 hover:bg-muted/20">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="font-medium text-foreground">{entry?.entry_number}</span>
                      {getStatusBadge(entry?.status)}
                      <span className="text-sm text-muted-foreground">
                        {new Date(entry?.entry_date)?.toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-foreground mb-1">{entry?.description}</p>
                    <p className="text-sm text-muted-foreground">
                      Created by {entry?.created_by?.full_name} • 
                      Debit: ${entry?.total_debit} • Credit: ${entry?.total_credit}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {entry?.status === 'draft' && (
                      <Button
                        onClick={() => handleApprove(entry?.id)}
                        size="sm"
                        iconName="CheckCircle"
                        variant="outline"
                      >
                        Approve
                      </Button>
                    )}
                    {entry?.status === 'approved' && (
                      <Button
                        onClick={() => handlePost(entry?.id)}
                        size="sm"
                        iconName="Send"
                        variant="outline"
                      >
                        Post
                      </Button>
                    )}
                    <Button
                      onClick={() => setSelectedEntry(entry)}
                      size="sm"
                      iconName="Eye"
                      variant="ghost"
                    >
                      View
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualJournalEntry;